import { camelCase, isArray, isPlainObject, snakeCase } from 'lodash-es';

import {
  isEnvironmentEphemeral,
  isEnvironmentProduction,
  isEnvironmentStaging,
} from '../Environment/utils';

import type { IErrorPayload } from './types';

export interface IErrorPayloadWithOptionalMessage extends Omit<IErrorPayload, 'message'> {
  message?: string;
  [key: string]: any;
}

export function addErrorMessage(error: IErrorPayloadWithOptionalMessage): IErrorPayload {
  const { status, message, ...otherProps } = error;
  const uniqueValues = new Set(Object.values(otherProps).flat(Infinity));
  const displayMessage = message ?? Array.from(uniqueValues).join(', ');

  return {
    status,
    message: displayMessage,
    ...otherProps,
  };
}

export function buildErrorMessageColorHub(error: IErrorPayloadWithOptionalMessage): IErrorPayload {
  const { status, message, ...otherProps } = error;
  const messageFromOtherProps = Object.keys(otherProps).length
    ? JSON.stringify(otherProps, null, 2)
    : '';
  return {
    status,
    message: message ?? messageFromOtherProps,
    ...otherProps,
  };
}

export function getError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  if (error instanceof Object) {
    return new Error(JSON.stringify(error));
  }
  return new Error(String(error));
}

type ConvertKeyFunction = (key: string) => string;

/**
 * Recursively converts all object keys given a converter function
 * Adapted from https://stackoverflow.com/a/50620653
 */
function convertObjectKeys(object: { [key: string]: any }, convertKey: ConvertKeyFunction): object {
  if (isPlainObject(object)) {
    return (Object.keys(object) as Array<string>).reduce(
      (result, key) => {
        // eslint-disable-next-line no-param-reassign
        result[convertKey(key)] = convertObjectKeys(object[key], convertKey);
        return result;
      },
      {} as { [key: string]: any }
    );
  }
  if (isArray(object)) {
    return object.map((value) => convertObjectKeys(value, convertKey));
  }
  return object;
}

/**
 * Converts all object keys to camelCase from snake_case. This is useful for converting from Python and Javascript object syntax.
 */
export function convertObjectKeysToCamelCase(object: object): object {
  return convertObjectKeys(object, camelCase);
}

/**
 * Converts all object keys to camelCase from snake_case. This is useful for converting from Javascript to Python object syntax.
 */
export function convertObjectKeysToSnakeCase(object: object): object {
  return convertObjectKeys(object, snakeCase);
}

// Return the CSRF Cookie name based on current environment. These names are configured in Django settings.
export function getCsrfCookieName(): string {
  if (isEnvironmentProduction()) {
    return 'csrftoken_prod';
  }
  if (isEnvironmentStaging()) {
    return 'csrftoken_staging';
  }
  if (isEnvironmentEphemeral()) {
    return 'csrftoken_ephemeral';
  }
  return 'csrftoken';
}
